import { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { statusBadge } from "@/lib/data";
import { useAnchoredMenu } from "./useAnchoredMenu";

interface InlineStatusBadgeProps {
  status: string;
  /** Statuses offered in the menu (include the current one to show it ticked) */
  options: string[];
  onChange: (newStatus: string) => Promise<void> | void;
  disabled?: boolean;
}

export default function InlineStatusBadge({ status, options, onChange, disabled }: InlineStatusBadgeProps) {
  const [loading, setLoading] = useState(false);
  // Fixed positioning so the menu isn't clipped by scrolling table containers
  const { open, setOpen, toggle, anchorRef, menuRef, style } = useAnchoredMenu(options.length * 40 + 12);
  const canChange = !disabled && options.some((o) => o !== status);

  const handleChange = async (newStatus: string) => {
    if (newStatus === status) { setOpen(false); return; }
    setLoading(true);
    setOpen(false);
    try { await onChange(newStatus); } finally { setLoading(false); }
  };

  return (
    <div className="relative inline-block">
      <button
        ref={anchorRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); if (canChange) toggle(); }}
        disabled={!canChange || loading}
        aria-haspopup={canChange ? "menu" : undefined}
        aria-expanded={canChange ? open : undefined}
        className={`inline-flex items-center gap-1 px-2.5 py-1 md:px-2 md:py-0.5 rounded-full text-xs font-medium capitalize transition-all ${statusBadge(status)} ${
          canChange ? "cursor-pointer hover:opacity-80 hover:shadow-sm" : "cursor-default"
        } ${loading ? "opacity-60" : ""}`}
      >
        {loading ? (
          <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          status.replace(/_/g, " ")
        )}
        {canChange && !loading && <ChevronDown className="w-2.5 h-2.5 opacity-60" />}
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          style={style}
          onClick={(e) => e.stopPropagation()}
          className="bg-white border border-stone-200 rounded-xl shadow-xl overflow-hidden min-w-[160px]"
        >
          <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-stone-400">Change to</div>
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              role="menuitem"
              onClick={() => handleChange(opt)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 md:py-2 text-xs text-left hover:bg-stone-50 transition-colors"
            >
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium capitalize ${statusBadge(opt)}`}>
                {opt.replace(/_/g, " ")}
              </span>
              {opt === status && <Check className="w-3 h-3 text-amber-600 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
